import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { HiOutlineDocumentAdd } from 'react-icons/hi';
import { useNavigate } from 'react-router';
import { Button } from '@src/components';
import { logError } from '@src/services/error-logger';
import { ORDER_STATE_CONFIG, OrderRowWithInfo } from '../utils/order-status';
import { NifOrderItem } from './OrderItem';
import { FiscalRepSubscriptionsItem } from './OrderItem/FiscalRepOrderItem';

interface Props {
  orders: OrderRowWithInfo[];
  emptyStateText: string;
}

function OrderList({ orders, emptyStateText }: Props): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!orders.length) {
    return (
      <div className="mt-8 flex flex-1 flex-col items-center justify-center px-8">
        <div className="text-sm font-medium text-gray-900">
          <HiOutlineDocumentAdd className="brdrio-stroke-1 mx-auto my-0 h-11 w-11 text-gray-400" />
          <div className="pt-3">{emptyStateText}</div>
        </div>
        <div className="mt-1 text-sm text-gray-500">
          {t(
            'dashboard.get_started_description_nif_only',
            'Get started by placing a new NIF order.',
          )}
        </div>
        <Button
          onClick={() => navigate('/orders/nif/place-order')}
          className="mt-6"
        >
          {t('buttons.order_nif', 'Order a NIF')}
        </Button>
      </div>
    );
  }

  const groupedOrdersWithInfo = Object.values(
    _.groupBy(orders, (order) => {
      if (
        order.row.__typename === 'FiscalRepSubscription' &&
        order.row.isActive
      ) {
        return order.row.stripeSubscriptionId ?? order.row.id;
      }
      return order.row.id;
    }),
  );

  const sortedOrders = _.orderBy(
    groupedOrdersWithInfo,
    [
      (orderArr) =>
        Math.min(
          ...orderArr.map(
            (order) => ORDER_STATE_CONFIG[order.info.state].priority,
          ),
        ),
      (orderArr) => orderArr[0].row.createdAt,
    ],
    ['asc', 'desc'],
  );

  return (
    <ul className="space-y-4">
      {sortedOrders.map((orderArr) => {
        const firstOrder = orderArr[0];
        switch (firstOrder.row.__typename) {
          case 'NifOrder':
            return (
              <NifOrderItem
                key={firstOrder.row.id}
                order={firstOrder.row}
                statusInfo={firstOrder.info}
              />
            );
          case 'FiscalRepSubscription':
            return (
              <FiscalRepSubscriptionsItem
                key={firstOrder.row.id}
                subscriptionsWithInfo={orderArr.map((order) => {
                  if (order.row.__typename !== 'FiscalRepSubscription') {
                    throw new Error(
                      `Expected FiscalRepSubscription, got ${order.row.__typename}`,
                    );
                  }
                  return {
                    row: order.row,
                    statusInfo: order.info,
                  };
                })}
              />
            );
          default:
            logError(
              new Error(`Unknown order type: ${firstOrder.row.__typename}`),
            );
            return null;
        }
      })}
    </ul>
  );
}

export default OrderList;
